import {
  AuthorityType,
  BankAccountIdentifierType,
  PayoutCreditorKind,
  PayoutPurposeCode,
  TransferType
} from '@sollapay/enums';
import {
  Money,
  isValidIsraeliBankCode,
  isValidIsraeliBankAccountNumber,
  isValidIsraeliBranchCode,
  parseIsraeliIban,
  parseMoney
} from '@sollapay/utils';

import type {AiSuggestion} from '@/features/ai-extraction';
import type {
  BeneficiaryLastAccountDTO,
  PaymentProviderDTO,
  PayoutCreditorDTO
} from '@sollapay/types';
import type {FieldSelectOption} from '@sollapay/ui/components';
import type {TFunction} from 'i18next';

/** How a bank account is entered — the two inputs a bank-paid payout accepts. */
export type BankAccountInputType =
  | BankAccountIdentifierType.IBAN
  | BankAccountIdentifierType.BANK_DETAILS;

/** The transfer-type select's value: a bank account input, or a bank check (no account at all). */
export type CreditorAccountType = BankAccountInputType | TransferType.BANK_CHECK;

/** The authority variants are `AuthorityType` itself, not a re-spelled shadow union — every consumer branches on the real enum. */
export type PayeeKind = 'none' | 'beneficiary' | AuthorityType;

export const AUTHORITY_PREFIX = 'authority:';

const MINIMUM_PAYOUT_AMOUNT_NIS = '1';

/** Parses the payee combobox's `authority:${code}` string-prefix value into a discriminated kind. */
export function resolvePayeeKind(beneficiaryId: string): PayeeKind {
  if (!beneficiaryId) return 'none';
  if (!beneficiaryId.startsWith(AUTHORITY_PREFIX)) return 'beneficiary';
  return beneficiaryId.slice(AUTHORITY_PREFIX.length) as AuthorityType;
}

/** Authorities aren't parties, so every authority kind shares one generic role label. */
export function resolvePayoutRoleLabel(
  creditor: PayoutCreditorDTO,
  t: TFunction<'trustAccounts'>
): string {
  return creditor.kind === PayoutCreditorKind.BENEFICIARY
    ? t(`partyRole.${creditor.role}`)
    : t('partyRole.authority');
}

/** No payee yet, or the Tax Authority (paid against a voucher, never by transfer) — no account, no rail. */
export function payeeHasBankAccount(kind: PayeeKind): boolean {
  return kind !== 'none' && kind !== AuthorityType.TAX_AUTHORITY;
}

export function isBankCheckAccountType(
  accountType: CreditorAccountType
): accountType is TransferType.BANK_CHECK {
  return accountType === TransferType.BANK_CHECK;
}

/** A check the user picked explicitly must survive a late autofill (reusable account, AI extraction). */
export function resolveAutofilledAccountType(
  current: CreditorAccountType,
  autofilled: CreditorAccountType
): CreditorAccountType {
  return isBankCheckAccountType(current) ? current : autofilled;
}

/** A check is chosen per payout, and only a payee that is paid by transfer can take one. */
export function isBankCheckPayout(values: {
  beneficiaryId: string;
  accountType: CreditorAccountType;
}): boolean {
  return (
    payeeHasBankAccount(resolvePayeeKind(values.beneficiaryId)) &&
    isBankCheckAccountType(values.accountType)
  );
}

const BANK_BRAND_ALIAS_PATTERN = /pepper|פפר/i;

/**
 * Drops digital-bank brands (Pepper) that share their parent bank's code (Leumi, 10): a payout
 * only needs the code, and a shared code makes the bank lookup pick whichever comes first.
 */
export function withoutBankBrandAliases(providers: PaymentProviderDTO[]): PaymentProviderDTO[] {
  return providers.filter(
    p => !BANK_BRAND_ALIAS_PATTERN.test(`${p.id} ${p.name} ${p.nameNativeLanguage}`)
  );
}

/** Real (non-sandbox) provider for an Israeli bank code — a fake bank can share a real bank's number. */
export function findBankProviderByBankCode(
  providers: PaymentProviderDTO[],
  bankCode: string
): PaymentProviderDTO | undefined {
  return providers.find(
    p => !p.isFakeBank && p.bankNumber != null && String(p.bankNumber) === bankCode
  );
}

export function isAuthorityPayeeKind(kind: PayeeKind): kind is AuthorityType {
  return kind !== 'none' && kind !== 'beneficiary';
}

export function isValidIban(accountNumber: string): boolean {
  return parseIsraeliIban(accountNumber) !== null;
}

/** Classifies a raw account identifier (e.g. from AI extraction) by shape. */
export function inferAccountTypeFromValue(value: string): CreditorAccountType {
  return isValidIban(value)
    ? BankAccountIdentifierType.IBAN
    : BankAccountIdentifierType.BANK_DETAILS;
}

/** Prioritizes a full bank_details trio over an iban fallback; returns undefined when neither landed. */
export function resolveAccountTypeFromSuggestions(params: {
  accountNumberSuggestion: AiSuggestion | undefined;
  bankCodeSuggestion: AiSuggestion | undefined;
  branchCodeSuggestion: AiSuggestion | undefined;
  bankAccountNumberSuggestion: AiSuggestion | undefined;
}): CreditorAccountType | undefined {
  const {
    accountNumberSuggestion,
    bankCodeSuggestion,
    branchCodeSuggestion,
    bankAccountNumberSuggestion
  } = params;
  const hasFullBankDetails =
    !!bankCodeSuggestion &&
    !bankCodeSuggestion.dismissed &&
    !!branchCodeSuggestion &&
    !branchCodeSuggestion.dismissed &&
    !!bankAccountNumberSuggestion &&
    !bankAccountNumberSuggestion.dismissed;

  if (hasFullBankDetails) return BankAccountIdentifierType.BANK_DETAILS;
  if (accountNumberSuggestion && !accountNumberSuggestion.dismissed) {
    return inferAccountTypeFromValue(accountNumberSuggestion.value);
  }
  return undefined;
}

/** Only a stored IBAN is reusable — a BBAN can't be split into the form's bank details. */
export function resolveReusableLastAccount(
  lastAccount: BeneficiaryLastAccountDTO | null | undefined
): BeneficiaryLastAccountDTO | undefined {
  return lastAccount?.accountType === BankAccountIdentifierType.IBAN ? lastAccount : undefined;
}

/** Masav is off the table at the zahav-only amount; a bank check is offered only to firms that opted in. */
export function buildAccountTypeOptions(
  t: TFunction<'trustAccounts'>,
  params: {isZahavOnly: boolean; isCheckEnabled: boolean}
): FieldSelectOption[] {
  const options: FieldSelectOption[] = [
    {
      value: BankAccountIdentifierType.IBAN,
      label: t('view.payouts.create.details.accountTypeOptions.iban')
    }
  ];
  if (!params.isZahavOnly) {
    options.unshift({
      value: BankAccountIdentifierType.BANK_DETAILS,
      label: t('view.payouts.create.details.accountTypeOptions.bankDetails')
    });
  }
  if (params.isCheckEnabled) {
    options.push({
      value: TransferType.BANK_CHECK,
      label: t('view.payouts.create.details.accountTypeOptions.bankCheck')
    });
  }
  return options;
}

export const isValidBankCode = isValidIsraeliBankCode;
export const isValidBranchCode = isValidIsraeliBranchCode;
export const isValidBankAccountNumber = isValidIsraeliBankAccountNumber;

export interface PayoutDetailsValues {
  beneficiaryId: string;
  amount: string;
  description: string;
  purposeCode: string;
  purposeOtherDetail: string;
  voucherNumber: string;
  authorityName: string;
  paymentReference: string;
  accountType: CreditorAccountType;
  accountNumber: string;
  bankCode: string;
  branchCode: string;
  bankAccountNumber: string;
  creditorDocumentId: string;
}

export interface PayoutDetailsErrorKeys {
  beneficiary?: 'beneficiaryRequired';
  amount?: 'amountRequired' | 'amountAboveZero' | 'amountExceedsBalance';
  description?: 'descriptionRequired';
  purposeCode?: 'purposeCodeRequired';
  purposeOtherDetail?: 'purposeOtherDetailRequired';
  voucherNumber?: 'voucherNumberRequired';
  authorityName?: 'authorityNameRequired';
  paymentReference?: 'paymentReferenceRequired';
  document?: 'documentRequired';
  accountNumber?: 'accountNumberRequired' | 'ibanInvalid';
  bankCode?: 'bankCodeRequired';
  branchCode?: 'branchCodeRequired';
  bankAccountNumber?: 'bankAccountNumberRequired';
}

type PayoutDetailsErrorKey = NonNullable<PayoutDetailsErrorKeys[keyof PayoutDetailsErrorKeys]>;

/** Error keys shown immediately rather than gated behind the Continue-click showErrors flag. */
export const LIVE_ERROR_KEYS: ReadonlySet<PayoutDetailsErrorKey> = new Set([
  'amountAboveZero',
  'amountExceedsBalance'
]);

/** Single source of truth for payout-details validity — shared by field-level error display and the Continue gate. */
export function getPayoutDetailsErrorKeys(
  values: PayoutDetailsValues,
  availableBalanceNis: string
): PayoutDetailsErrorKeys {
  const errors: PayoutDetailsErrorKeys = {};
  const payeeKind = resolvePayeeKind(values.beneficiaryId);

  if (!values.beneficiaryId) errors.beneficiary = 'beneficiaryRequired';

  if (!values.amount || values.amount === '0') {
    errors.amount = 'amountRequired';
  } else if (Money.from(values.amount).lt(MINIMUM_PAYOUT_AMOUNT_NIS)) {
    errors.amount = 'amountAboveZero';
  } else if (Money.from(values.amount).gt(parseMoney(availableBalanceNis))) {
    errors.amount = 'amountExceedsBalance';
  }

  const isBankCheck = isBankCheckPayout(values);

  if (!values.creditorDocumentId && !isBankCheck) errors.document = 'documentRequired';

  if (isAuthorityPayeeKind(payeeKind)) {
    if (!values.purposeCode) errors.purposeCode = 'purposeCodeRequired';
    if (values.purposeCode === PayoutPurposeCode.OTHER && !values.purposeOtherDetail.trim()) {
      errors.purposeOtherDetail = 'purposeOtherDetailRequired';
    }
  }

  if (payeeKind === AuthorityType.TAX_AUTHORITY) {
    if (!values.voucherNumber.trim()) errors.voucherNumber = 'voucherNumberRequired';
  } else if (
    payeeKind === AuthorityType.MUNICIPALITY ||
    payeeKind === AuthorityType.ISRAEL_LAND_AUTHORITY
  ) {
    if (payeeKind === AuthorityType.MUNICIPALITY && !values.authorityName.trim()) {
      errors.authorityName = 'authorityNameRequired';
    }
    if (!values.paymentReference.trim()) {
      errors.paymentReference = 'paymentReferenceRequired';
    }
  } else if (!values.description.trim()) {
    errors.description = 'descriptionRequired';
  }

  if (payeeHasBankAccount(payeeKind) && !isBankCheck) {
    if (values.accountType === BankAccountIdentifierType.BANK_DETAILS) {
      if (!values.bankCode || !isValidBankCode(values.bankCode)) {
        errors.bankCode = 'bankCodeRequired';
      }
      if (!values.branchCode || !isValidBranchCode(values.branchCode)) {
        errors.branchCode = 'branchCodeRequired';
      }
      if (!values.bankAccountNumber || !isValidBankAccountNumber(values.bankAccountNumber)) {
        errors.bankAccountNumber = 'bankAccountNumberRequired';
      }
    } else if (!values.accountNumber) {
      errors.accountNumber = 'accountNumberRequired';
    } else if (!isValidIban(values.accountNumber)) {
      errors.accountNumber = 'ibanInvalid';
    }
  }

  return errors;
}

export function isPayoutDetailsValid(errors: PayoutDetailsErrorKeys): boolean {
  return Object.keys(errors).length === 0;
}
